import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { uploadMedia, mediaUrl } from "@/lib/upload";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Camera, Video, ImagePlus, Trash2, X, Play, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// Capture buttons: camera photo / camera video / pick from device. Mobile browsers open the camera via `capture`.
export function CaptureButtons({ onFiles, disabled, size = "md" }) {
  const photo = useRef(); const video = useRef(); const pick = useRef();
  const pad = size === "sm" ? "px-2.5 py-2 text-[11px]" : "px-3 py-2.5 text-xs";
  const btn = `inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border font-bold uppercase tracking-wide hover:bg-accent active:scale-[0.98] disabled:opacity-50 sm:flex-none ${pad}`;
  const handle = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) onFiles(fs); };
  return (
    <div className="flex flex-wrap gap-2">
      <input ref={photo} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid="capture-photo-input" />
      <input ref={video} type="file" accept="video/*" capture="environment" className="hidden" onChange={handle} data-testid="capture-video-input" />
      <input ref={pick} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handle} data-testid="pick-media-input" />
      <button type="button" disabled={disabled} className={btn} onClick={() => photo.current.click()} data-testid="capture-photo-btn"><Camera size={15} /> Photo</button>
      <button type="button" disabled={disabled} className={btn} onClick={() => video.current.click()} data-testid="capture-video-btn"><Video size={15} /> Video</button>
      <button type="button" disabled={disabled} className={btn} onClick={() => pick.current.click()} data-testid="pick-media-btn"><ImagePlus size={15} /> Gallery / Files</button>
    </div>
  );
}

// Deferred queue used inside create forms (job not yet created). Parent uploads after save via uploadQueued().
export function PendingMedia({ files, onChange }) {
  const remove = (i) => onChange(files.filter((_, k) => k !== i));
  return (
    <div className="space-y-2">
      <CaptureButtons onFiles={(fs) => onChange([...files, ...fs])} />
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="pending-media-grid">
          {files.map((f, i) => (
            <div key={i} className="relative aspect-square overflow-hidden rounded-md border border-border bg-black/40" data-testid={`pending-media-${i}`}>
              {f.type.startsWith("video/") ? (
                <div className="flex h-full flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground"><Video size={18} /> {fmtSize(f.size)}</div>
              ) : <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />}
              <button type="button" onClick={() => remove(i)} className="absolute right-1 top-1 rounded bg-black/70 p-0.5 text-white" data-testid={`pending-media-remove-${i}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && <p className="text-[11px] text-muted-foreground">{files.length} file{files.length > 1 ? "s" : ""} will upload after saving.</p>}
    </div>
  );
}

export async function uploadQueued(files, target, label = "media") {
  if (!files?.length) return;
  const id = toast.loading(`Uploading ${files.length} ${label}…`);
  let ok = 0;
  for (const f of files) {
    try { await uploadMedia(f, target); ok++; } catch (e) { toast.error(`${f.name}: ${apiError(e.response?.data?.detail)}`); }
  }
  toast.success(`${ok}/${files.length} ${label} uploaded`, { id });
}

// Live gallery + uploader bound to an existing entity.
export default function MediaUploader({ entityType, entityId, stage, title, compact }) {
  const { can } = useAuth();
  const canDelete = can("records.delete");
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({});
  const [view, setView] = useState(null);

  const load = useCallback(() => {
    api.get("/media", { params: { entity_type: entityType, entity_id: entityId, ...(stage ? { stage } : {}) } })
      .then((r) => setItems(r.data)).catch(() => {});
  }, [entityType, entityId, stage]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    for (const f of files) {
      const key = `${f.name}-${f.size}-${Date.now()}`;
      setProgress((p) => ({ ...p, [key]: { name: f.name, pct: 0 } }));
      try {
        await uploadMedia(f, { entityType, entityId, stage: stage || "repair" }, (pct) => setProgress((p) => ({ ...p, [key]: { name: f.name, pct } })));
        toast.success(`${f.name || "Capture"} uploaded`);
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      setProgress((p) => { const n = { ...p }; delete n[key]; return n; });
      load();
    }
  };

  const remove = async (m) => {
    try { await api.delete(`/media/${m.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uploading = Object.values(progress);
  return (
    <div className="space-y-3" data-testid={`media-uploader-${stage || "all"}`}>
      {title && <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{title}</div>}
      <CaptureButtons onFiles={onFiles} size={compact ? "sm" : "md"} />
      {uploading.map((u, i) => (
        <div key={i} className="rounded-md border border-border p-2 text-xs" data-testid="upload-progress">
          <div className="mb-1 flex items-center justify-between gap-2"><span className="flex items-center gap-1 truncate"><Loader2 size={12} className="animate-spin" /> {u.name}</span><span className="font-mono">{u.pct}%</span></div>
          <div className="h-1.5 overflow-hidden rounded bg-accent"><div className="h-full bg-primary transition-[width]" style={{ width: `${u.pct}%` }} /></div>
        </div>
      ))}
      {items.length === 0 && uploading.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="media-empty">No photos or videos yet. Use the camera on your phone or pick files.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5" data-testid="media-grid">
          {items.map((m) => (
            <button type="button" key={m.id} onClick={() => setView(m)} data-testid={`media-item-${m.id}`}
              className="group relative aspect-square overflow-hidden rounded-md border border-border bg-black/40 text-left">
              {m.kind === "video" ? (
                <video src={mediaUrl(m.id)} muted preload="metadata" className="h-full w-full object-cover" />
              ) : <img src={mediaUrl(m.id)} alt={m.caption || m.original_filename} loading="lazy" className="h-full w-full object-cover" />}
              {m.kind === "video" && <span className="absolute inset-0 flex items-center justify-center text-white"><Play size={22} className="drop-shadow" /></span>}
              <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1 py-0.5 font-mono text-[9px] uppercase text-white">{m.stage} · {fmtSize(m.size)}</span>
            </button>
          ))}
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-[120] flex flex-col bg-black/95" onClick={() => setView(null)} data-testid="media-lightbox">
          <div className="flex items-center justify-between gap-2 p-3 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0 text-xs"><div className="truncate font-semibold">{view.original_filename}</div><div className="font-mono text-[10px] text-white/60">{view.stage} · {view.uploaded_by_name} · {fmtDate(view.created_at)} · {fmtSize(view.size)}</div></div>
            <div className="flex gap-2">
              {canDelete && <button onClick={() => { remove(view); setView(null); }} className="rounded border border-white/30 p-2 hover:bg-white/10" data-testid="media-delete-btn"><Trash2 size={16} /></button>}
              <button onClick={() => setView(null)} className="rounded border border-white/30 p-2 hover:bg-white/10" data-testid="media-lightbox-close"><X size={16} /></button>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-hidden p-2" onClick={(e) => e.stopPropagation()}>
            {view.kind === "video"
              ? <video src={mediaUrl(view.id)} controls autoPlay playsInline className="max-h-full max-w-full" />
              : <img src={mediaUrl(view.id)} alt="" className="max-h-full max-w-full object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}
